/**
 * Der Renderer. Nimmt Schnappschüsse entgegen und zeichnet — er schreibt NIE
 * in die Simulation zurück (docs/2026-07-25-architektur.md §1).
 *
 * Die Karte ändert sich während eines Abstiegs nicht und wird deshalb einmal
 * auf eine Hintergrundfläche gezeichnet und danach nur noch ausschnittweise
 * kopiert. Das ist der Grund, warum wir mit Canvas 2D im Budget bleiben.
 */

import type { BeuteSicht, EffektSicht, Schnappschuss, Seltenheit, Vek } from '@pg/protocol';
import type { Karte } from '@pg/sim';
import { FELS, WAHRZEICHEN_BODEN } from '@pg/sim';
import { zeichneFigur, zeichneKruemel, type Farben, type Grundform } from './figuren.js';

export interface Kamera {
  /** Weltposition der Bildmitte. */
  x: number;
  y: number;
  /** Pixel je Welteinheit. */
  masstab: number;
}

const GESTALT: Record<string, { form: Grundform; hoehe: number; breite: number; segmente: number }> = {
  russkaefer: { form: 'kaefer', hoehe: 0.55, breite: 0.9, segmente: 9 },
  schlackenkriecher: { form: 'klumpen', hoehe: 1.1, breite: 1.2, segmente: 11 },
  glutmotte: { form: 'flatterer', hoehe: 0.8, breite: 1.1, segmente: 7 },
  aschespucker: { form: 'gebeugt', hoehe: 1.0, breite: 0.8, segmente: 10 },
  kesselwart: { form: 'aufrecht', hoehe: 1.6, breite: 1.3, segmente: 13 },
  'der-schmelzer': { form: 'aufrecht', hoehe: 2.2, breite: 1.8, segmente: 14 },
  kesselschmied: { form: 'held', hoehe: 1.35, breite: 1.0, segmente: 12 },
};

/** Wie viel groesser ein Anfuehrer gezeichnet wird. */
function hoehenFaktor(rang: string): number {
  if (rang === 'gezeichneter') return 1.35;
  if (rang === 'vorarbeiter') return 1.18;
  return 1;
}

export const SELTENHEIT_FARBE: Record<Seltenheit, string> = {
  schlicht: '--tinte',
  gezeichnet: '--span',
  verschriftet: '--messing',
  gepraegt: '--glut',
  reliquie: '--messing',
};

export class Szene {
  #farben: Farben;

  constructor(farben: Farben) {
    this.#farben = farben;
  }

  setzeFarben(f: Farben): void {
    this.#farben = f;
  }

  /**
   * Die Karte wird pro Bild gezeichnet — aber nur der sichtbare Ausschnitt.
   *
   * Der erste Entwurf hat die ganze Karte einmal auf eine Hintergrundfläche
   * gerendert und daraus kopiert. Das sah bei starkem Zoom klobig aus (eine
   * 90x90-Karte müsste sonst mit ~2900 px Kantenlänge vorgehalten werden, also
   * gut 30 MB) und wurde deshalb verworfen. Sichtbar sind nie mehr als etwa
   * 12x18 Zellen; die direkt zu zeichnen kostet nichts und ist bei jedem
   * Maßstab gestochen.
   */
  #zeichneKarte(
    ctx: CanvasRenderingContext2D,
    karte: Karte,
    kamera: Kamera,
    breite: number,
    hoehe: number,
  ): void {
    const f = this.#farben;
    const m = kamera.masstab;
    const x0 = Math.max(0, Math.floor(kamera.x - breite / 2 / m) - 1);
    const x1 = Math.min(karte.breite - 1, Math.ceil(kamera.x + breite / 2 / m) + 1);
    const y0 = Math.max(0, Math.floor(kamera.y - hoehe / 2 / m) - 1);
    const y1 = Math.min(karte.hoehe - 1, Math.ceil(kamera.y + hoehe / 2 / m) + 1);

    const bx = (wx: number): number => (wx - kamera.x) * m + breite / 2;
    const by = (wy: number): number => (wy - kamera.y) * m + hoehe / 2;

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const z = karte.zellen[y * karte.breite + x];
        if (z === FELS) continue;
        ctx.fillStyle = z === WAHRZEICHEN_BODEN ? f.wahrzeichen : f.papier;
        // +1 px Ueberlappung, sonst blitzen bei Bruchteilen Fugen durch.
        ctx.fillRect(bx(x), by(y), m + 1, m + 1);
      }
    }

    // Schraffur im Fels entlang der Kante. Ohne sie ist der Fels eine flache
    // schwarze Masse — und "Textur nur durch Schraffur" ist die Kernregel der
    // Art Direction (§2.3). Nur Zellen direkt an der Grenze, das reicht optisch
    // vollstaendig und kostet fast nichts.
    ctx.save();
    ctx.strokeStyle = f.papier;
    ctx.globalAlpha = 0.1;
    ctx.lineWidth = Math.max(0.8, m * 0.025);
    ctx.beginPath();
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (karte.zellen[y * karte.breite + x] !== FELS) continue;
        // Nur Fels mit mindestens einem begehbaren Nachbarn.
        let amRand = false;
        for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0], [1, 1], [-1, -1], [1, -1], [-1, 1]] as const) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= karte.breite || ny >= karte.hoehe) continue;
          if (karte.zellen[ny * karte.breite + nx] !== FELS) { amRand = true; break; }
        }
        if (!amRand) continue;
        const px = bx(x);
        const py = by(y);
        // Diagonale Striche, Versatz aus den Zellkoordinaten — deterministisch,
        // damit sich das Muster beim Scrollen nicht bewegt.
        const versatz = ((x * 7 + y * 13) % 5) * (m / 5);
        for (let i = -m; i < m; i += m / 3.5) {
          ctx.moveTo(px + i + versatz, py);
          ctx.lineTo(px + i + versatz + m, py + m);
        }
      }
    }
    ctx.stroke();
    ctx.restore();

    // Bodenstruktur: einzelne kurze Striche, sehr zurueckhaltend. Verhindert,
    // dass grosse Kammern wie leeres Papier wirken.
    ctx.save();
    ctx.strokeStyle = f.tinte;
    ctx.globalAlpha = 0.07;
    ctx.lineWidth = Math.max(0.8, m * 0.022);
    ctx.beginPath();
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (karte.zellen[y * karte.breite + x] === FELS) continue;
        const streu = (x * 31 + y * 17) % 7;
        if (streu > 2) continue;
        const px = bx(x) + ((x * 13 + y * 5) % 10) * (m / 10);
        const py = by(y) + ((x * 5 + y * 11) % 10) * (m / 10);
        ctx.moveTo(px, py);
        ctx.lineTo(px + m * (streu === 0 ? 0.3 : 0.16), py + m * 0.05);
      }
    }
    ctx.stroke();
    ctx.restore();

    // Konturen an jeder Fels-Boden-Grenze — das ist, was den Tuschelook macht.
    ctx.save();
    const staerke = Math.max(2.2, m * 0.07);
    ctx.strokeStyle = f.tinte;
    ctx.lineWidth = staerke;
    ctx.lineCap = 'square';
    ctx.beginPath();
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (karte.zellen[y * karte.breite + x] === FELS) continue;
        const px = bx(x);
        const py = by(y);
        const fels = (dx: number, dy: number): boolean =>
          x + dx < 0 || y + dy < 0 || x + dx >= karte.breite || y + dy >= karte.hoehe ||
          karte.zellen[(y + dy) * karte.breite + (x + dx)] === FELS;
        // Die Kontur wird auf die PAPIERSEITE versetzt. Mittig auf der Grenze
        // gezeichnet verschwindet die halbe Linie im fast gleich dunklen Fels,
        // und der Tuschelook geht verloren.
        const i = staerke / 2;
        if (fels(0, -1)) { ctx.moveTo(px, py + i); ctx.lineTo(px + m, py + i); }
        if (fels(0, 1)) { ctx.moveTo(px, py + m - i); ctx.lineTo(px + m, py + m - i); }
        if (fels(-1, 0)) { ctx.moveTo(px + i, py); ctx.lineTo(px + i, py + m); }
        if (fels(1, 0)) { ctx.moveTo(px + m - i, py); ctx.lineTo(px + m - i, py + m); }
      }
    }
    ctx.stroke();
    ctx.restore();

    // Wahrzeichen: ein wiederholtes kleines Motiv statt eines Riesenkreises.
    // Ein bildschirmfuellender Kreis liest sich als Debug-Overlay, nicht als Bauwerk.
    for (const raum of karte.raeume) {
      if (raum.art !== 'wahrzeichen') continue;
      ctx.save();
      ctx.beginPath();
      ctx.rect(bx(raum.x), by(raum.y), raum.b * m, raum.h * m);
      ctx.clip();
      ctx.strokeStyle = f.tinte;
      ctx.globalAlpha = 0.3;
      ctx.lineWidth = Math.max(1, m * 0.035);
      for (let y = raum.y + 1; y < raum.y + raum.h - 1; y += 3) {
        for (let x = raum.x + 1; x < raum.x + raum.b - 1; x += 3) {
          const px = bx(x + 0.5);
          const py = by(y + 0.5);
          ctx.beginPath();
          ctx.moveTo(px - m * 0.28, py);
          ctx.lineTo(px + m * 0.28, py);
          ctx.moveTo(px, py - m * 0.28);
          ctx.lineTo(px, py + m * 0.28);
          ctx.stroke();
        }
      }
      ctx.restore();
    }
  }

  zeichne(
    ctx: CanvasRenderingContext2D,
    s: Schnappschuss,
    kamera: Kamera,
    breite: number,
    hoehe: number,
    zeit: number,
    effekte: readonly EffektSicht[],
    karte: Karte,
  ): void {
    const f = this.#farben;

    ctx.fillStyle = f.fels;
    ctx.fillRect(0, 0, breite, hoehe);

    const m = kamera.masstab;
    const zuBildX = (wx: number): number => (wx - kamera.x) * m + breite / 2;
    const zuBildY = (wy: number): number => (wy - kamera.y) * m + hoehe / 2;

    this.#zeichneKarte(ctx, karte, kamera, breite, hoehe);

    // Der Förderkorb der Zunft — der Ausgang, sobald der Ausklang gefallen ist.
    // Er wartet, statt den Abstieg sofort zu beenden.
    if (s.abstieg.ausgang) {
      const x = zuBildX(s.abstieg.ausgang.x);
      const y = zuBildY(s.abstieg.ausgang.y);
      const puls = 0.75 + Math.sin(zeit * 2) * 0.25;
      ctx.save();
      ctx.globalAlpha = 0.3 * puls;
      ctx.fillStyle = f.glut;
      ctx.beginPath();
      ctx.ellipse(x, y, m * 1.5, m * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      // Seile nach oben, Korb aus Weidengeflecht.
      ctx.strokeStyle = f.tinte;
      ctx.lineWidth = Math.max(2, m * 0.06);
      ctx.beginPath();
      ctx.moveTo(x - m * 0.45, y - m * 0.7);
      ctx.lineTo(x - m * 0.2, y - m * 2.6);
      ctx.moveTo(x + m * 0.45, y - m * 0.7);
      ctx.lineTo(x + m * 0.2, y - m * 2.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.rect(x - m * 0.55, y - m * 0.75, m * 1.1, m * 0.75);
      ctx.fillStyle = f.flaeche;
      ctx.fill();
      ctx.stroke();
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(x - m * 0.55, y - m * 0.75 + (m * 0.75 * i) / 4);
        ctx.lineTo(x + m * 0.55, y - m * 0.75 + (m * 0.75 * i) / 4);
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.restore();
    }

    // Beute liegt unter allem anderen
    for (const b of s.beute) zeichneBeute(ctx, f, b, zuBildX(b.pos.x), zuBildY(b.pos.y), m, zeit);

    // Namensschilder fuer anklickbare Beute. Ohne sie weiss niemand, was da
    // liegt und dass man es anfassen muss.
    for (const b of s.beute) {
      if (!b.anklicken) continue;
      const x = zuBildX(b.pos.x);
      const y = zuBildY(b.pos.y) - m * 0.95;
      const farbe = beuteFarbe(f, b.seltenheit, b.gattung);
      const text = b.menge > 1 ? `${b.name} ×${b.menge}` : b.name;
      ctx.save();
      ctx.font = `500 ${Math.round(Math.max(11, m * 0.21))}px "IBM Plex Mono", monospace`;
      ctx.textAlign = 'center';
      const breiteText = ctx.measureText(text).width;
      ctx.globalAlpha = b.inReichweite ? 0.96 : 0.6;
      ctx.fillStyle = 'rgba(15,13,10,.82)';
      ctx.fillRect(x - breiteText / 2 - 6, y - 13, breiteText + 12, 18);
      ctx.strokeStyle = farbe;
      ctx.lineWidth = b.inReichweite ? 1.6 : 1;
      ctx.strokeRect(x - breiteText / 2 - 6, y - 13, breiteText + 12, 18);
      ctx.fillStyle = farbe;
      ctx.fillText(text, x, y);
      ctx.restore();
    }

    // Telegrafen liegen unter den Figuren, damit sie lesbar bleiben. Sie werden
    // von der Simulation jeden Takt neu gesendet — hier zaehlt deshalb nur der
    // aktuelle Takt, sonst bleiben Geisterringe stehen.
    for (const e of effekte) {
      if (e.art !== 'telegraf' || e.takt !== s.takt) continue;
      const x = zuBildX(e.pos.x);
      const y = zuBildY(e.pos.y);
      const r = e.wert * m;
      ctx.save();
      // Flaeche andeuten, Rand betonen: man muss auf einen Blick sehen,
      // wo man gleich NICHT stehen sollte.
      ctx.globalAlpha = 0.14;
      ctx.fillStyle = f.glut;
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 0.72, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.75;
      ctx.strokeStyle = f.glut;
      ctx.lineWidth = Math.max(2, m * 0.06);
      ctx.setLineDash([m * 0.22, m * 0.16]);
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 0.72, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Wesen, von hinten nach vorn
    const sortiert = [...s.wesen, s.spieler].sort((a, b) => a.pos.y - b.pos.y);
    for (const w of sortiert) {
      const x = zuBildX(w.pos.x);
      const y = zuBildY(w.pos.y);
      if (x < -120 || y < -120 || x > breite + 120 || y > hoehe + 120) continue;

      if (w.art === 'begleiter') {
        zeichneKruemel(
          ctx,
          f,
          { x, y, masstab: m, winkel: w.winkel, zeit, bewegung: 1, aktion: 0, hoehe: 0.6, breite: 0.5, segmente: 8 },
          w.aktion,
        );
        continue;
      }

      const g0 = GESTALT[w.bauplan] ?? GESTALT['russkaefer']!;
      // Anfuehrer sind sichtbar groesser — der erste Hinweis kommt aus der
      // Silhouette, nicht aus einem Balken.
      const skala = hoehenFaktor(w.rang);
      const g = { ...g0, hoehe: g0.hoehe * skala, breite: g0.breite * skala };

      // Der Held bekommt eine Bodenmarke — sonst findet man sich im Getümmel nicht.
      if (w.art === 'spieler') {
        ctx.save();
        ctx.strokeStyle = f.glut;
        ctx.globalAlpha = 0.55;
        ctx.lineWidth = Math.max(1.6, m * 0.05);
        ctx.beginPath();
        ctx.ellipse(x, y, m * 0.5, m * 0.2, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      zeichneFigur(ctx, f, g.form, {
        x,
        y,
        masstab: m,
        winkel: w.winkel,
        zeit,
        bewegung: w.haltung === 'gehen' ? 1 : 0.12,
        aktion: w.aktion,
        hoehe: g.hoehe,
        breite: g.breite,
        segmente: g.segmente,
      });

      // Lebensbalken nur bei Verwundeten und Anfuehrern — sonst ist der Schirm voll.
      if (w.art === 'gegner' && (w.benannt || w.leben < w.lebenMax)) {
        const bw = Math.max(24, g.breite * m * 0.9);
        const by = y - g.hoehe * m * hoehenFaktor(w.rang) - 10;
        ctx.fillStyle = f.papier;
        ctx.fillRect(x - bw / 2, by, bw, 4);
        ctx.fillStyle = w.benannt ? f.glut : f.tinte;
        ctx.fillRect(x - bw / 2, by, bw * Math.max(0, w.leben / w.lebenMax), 4);
        ctx.strokeStyle = f.tinte;
        ctx.lineWidth = 1;
        ctx.strokeRect(x - bw / 2, by, bw, 4);

        // Namensschild nur fuer Anfuehrer. Man muss auf einen Blick sehen,
        // WARUM dieser Gegner anders ist (Ueberarbeitung B6).
        if (w.anzeigename) {
          ctx.save();
          ctx.font = `500 ${Math.round(Math.max(10, m * 0.2))}px "IBM Plex Mono", monospace`;
          ctx.textAlign = 'center';
          ctx.lineJoin = 'round';
          ctx.lineWidth = 3;
          ctx.strokeStyle = f.tinte;
          ctx.strokeText(w.anzeigename, x, by - 6);
          ctx.fillStyle = w.rang === 'gezeichneter' ? f.glut : f.messing;
          ctx.fillText(w.anzeigename, x, by - 6);
          ctx.restore();
        }
      }
    }

    // Geschosse
    for (const p of s.geschosse) {
      const x = zuBildX(p.pos.x);
      const y = zuBildY(p.pos.y);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(p.winkel);
      ctx.beginPath();
      ctx.moveTo(-m * 0.18, 0);
      ctx.lineTo(m * 0.18, 0);
      ctx.strokeStyle = f.tinte;
      ctx.lineWidth = Math.max(2, m * 0.07);
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.restore();
    }

    // Schlag-, Treffer- und Todeseffekte. Zahlen aus demselben Takt werden
    // gegeneinander versetzt, sonst steht im Getümmel ein unlesbarer Klumpen.
    const versatz = new Map<number, number>();
    for (const e of effekte) {
      const alter = (s.takt - e.takt) / 30;
      const x = zuBildX(e.pos.x);
      const y = zuBildY(e.pos.y);
      let stapel = 0;
      if (e.art === 'treffer' || e.art === 'kritisch') {
        stapel = versatz.get(e.takt) ?? 0;
        versatz.set(e.takt, stapel + 1);
      }
      zeichneEffekt(ctx, f, e, x + stapel * m * 0.42, y - stapel * m * 0.3, m, alter);
    }
  }
}

function zeichneEffekt(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  e: EffektSicht,
  x: number,
  y: number,
  m: number,
  alter: number,
): void {
  switch (e.art) {
    case 'schlag': {
      // Kurz und knapp innerhalb der tatsaechlichen Reichweite. Ein Ring, der
      // ueber die Reichweite hinauswaechst, verspricht Wirkung, die es nicht gibt.
      const dauer = 0.22;
      if (alter > dauer) return;
      const t = alter / dauer;
      ctx.save();
      ctx.globalAlpha = 0.75 * (1 - t);
      ctx.strokeStyle = f.glut;
      ctx.lineWidth = Math.max(2, m * 0.08) * (1 - t * 0.5);
      ctx.beginPath();
      ctx.arc(x, y, e.wert * m * (0.45 + t * 0.55), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'treffer':
    case 'kritisch': {
      if (alter > 0.85) return;
      // Zahlen erscheinen ueber hellem Boden UND ueber dunklem Fels. Ohne
      // Kontur ist die eine oder die andere Haelfte immer unlesbar.
      ctx.save();
      ctx.globalAlpha = 1 - alter / 0.85;
      const gross = e.art === 'kritisch';
      ctx.font = `${gross ? 600 : 500} ${Math.round(m * (gross ? 0.34 : 0.26))}px "IBM Plex Mono", monospace`;
      ctx.textAlign = 'center';
      ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(2.5, m * 0.07);
      ctx.strokeStyle = f.tinte;
      const text = String(Math.round(e.wert));
      const ty = y - m * 0.9 - alter * 24;
      ctx.strokeText(text, x, ty);
      ctx.fillStyle = gross ? f.glut : f.papier;
      ctx.fillText(text, x, ty);
      ctx.restore();
      break;
    }
    case 'tod': {
      if (alter > 0.6) return;
      ctx.save();
      ctx.globalAlpha = 0.5 * (1 - alter / 0.6);
      ctx.fillStyle = f.tinte;
      for (let i = 0; i < 6; i++) {
        const w = (i / 6) * Math.PI * 2;
        const r = e.wert * m * (0.4 + alter * 2.2);
        ctx.beginPath();
        ctx.arc(x + Math.cos(w) * r, y + Math.sin(w) * r * 0.6, m * 0.08, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      break;
    }
    case 'aufgehoben': {
      if (alter > 0.5) return;
      ctx.save();
      ctx.globalAlpha = 1 - alter / 0.5;
      ctx.strokeStyle = f.messing;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y - alter * 30, m * 0.3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'stufe': {
      if (alter > 1.4) return;
      ctx.save();
      ctx.globalAlpha = 1 - alter / 1.4;
      ctx.fillStyle = f.glut;
      ctx.font = `600 ${Math.round(m * 0.5)}px Fraunces, serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`Stufe ${e.wert}`, x, y - m * 1.6 - alter * 30);
      ctx.restore();
      break;
    }
  }
}

function zeichneBeute(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  b: BeuteSicht,
  x: number,
  y: number,
  m: number,
  zeit: number,
): void {
  const farbe = beuteFarbe(f, b.seltenheit, b.gattung);
  const puls = 0.85 + Math.sin(zeit * 2.4 + b.id) * 0.15;

  // Lichtschein am Boden, ohne Weichzeichner: konzentrische Ringe.
  ctx.save();
  ctx.globalAlpha = 0.22 * puls;
  ctx.fillStyle = farbe;
  ctx.beginPath();
  ctx.ellipse(x, y, m * 0.55, m * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Das Stück selbst: eine kleine Marke mit Kontur.
  ctx.save();
  ctx.translate(x, y - m * 0.18);
  ctx.beginPath();
  if (b.gattung === 'fladen') {
    // Ein Fladen: runde Scheibe mit Kerbe. Heilt beim Darueberlaufen.
    ctx.arc(0, 0, m * 0.19, 0, Math.PI * 2);
  } else if (b.gattung === 'siegel') {
    ctx.arc(0, 0, m * 0.17, 0, Math.PI * 2);
  } else if (b.gattung === 'baugut') {
    ctx.rect(-m * 0.18, -m * 0.12, m * 0.36, m * 0.24);
  } else if (b.gattung === 'material') {
    ctx.moveTo(0, -m * 0.16);
    ctx.lineTo(m * 0.15, m * 0.1);
    ctx.lineTo(-m * 0.15, m * 0.1);
    ctx.closePath();
  } else {
    ctx.moveTo(0, -m * 0.2);
    ctx.lineTo(m * 0.16, 0);
    ctx.lineTo(0, m * 0.2);
    ctx.lineTo(-m * 0.16, 0);
    ctx.closePath();
  }
  ctx.fillStyle = farbe;
  ctx.fill();
  ctx.strokeStyle = f.tinte;
  ctx.lineWidth = Math.max(1.4, m * 0.05);
  ctx.stroke();

  // Senkrechte Marke — ersetzt die Lichtsäule, ohne Blur.
  ctx.globalAlpha = 0.5 * puls;
  ctx.beginPath();
  ctx.moveTo(0, -m * 0.3);
  ctx.lineTo(0, -m * 0.95);
  ctx.strokeStyle = farbe;
  ctx.lineWidth = Math.max(1.5, m * 0.05);
  ctx.stroke();
  ctx.restore();
}

function beuteFarbe(f: Farben, s: Seltenheit, gattung?: string): string {
  if (gattung === 'fladen') return f.glut;
  switch (s) {
    case 'gepraegt':
    case 'reliquie':
      return f.glut;
    case 'verschriftet':
      return f.messing;
    case 'gezeichnet':
      return f.span;
    default:
      return f.tinte;
  }
}

/**
 * Welches Beutestück liegt unter diesem Bildschirmpunkt?
 *
 * Grosszuegige Trefferflaeche: Auf einem Telefon trifft niemand ein Symbol von
 * zwanzig Pixeln. Bevorzugt wird, was in Reichweite liegt.
 */
export function beuteUnterPunkt(
  s: Schnappschuss,
  kamera: Kamera,
  breite: number,
  hoehe: number,
  bx: number,
  by: number,
): number | null {
  const m = kamera.masstab;
  let beste: number | null = null;
  let besteEntfernung = Math.max(46, m * 0.9);

  for (const b of s.beute) {
    if (!b.anklicken) continue;
    const x = (b.pos.x - kamera.x) * m + breite / 2;
    const y = (b.pos.y - kamera.y) * m + hoehe / 2;
    // Auch das Namensschild ueber dem Stueck zaehlt als Trefferflaeche.
    const d = Math.min(Math.hypot(bx - x, by - y), Math.hypot(bx - x, by - (y - m * 0.95)));
    const gewichtet = b.inReichweite ? d : d + 30;
    if (gewichtet < besteEntfernung) {
      besteEntfernung = gewichtet;
      beste = b.id;
    }
  }
  return beste;
}

/** Minikarte — ohne sie verläuft man sich in den Gängen. */
export function zeichneMinikarte(
  ctx: CanvasRenderingContext2D,
  karte: Karte,
  spieler: Vek,
  ziel: Vek | null,
  f: Farben,
  groesse: number,
): void {
  const skala = groesse / Math.max(karte.breite, karte.hoehe);
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = f.papier;
  ctx.fillRect(0, 0, karte.breite * skala, karte.hoehe * skala);

  ctx.fillStyle = f.schatten;
  for (let y = 0; y < karte.hoehe; y++) {
    for (let x = 0; x < karte.breite; x++) {
      if (karte.zellen[y * karte.breite + x] === FELS) {
        ctx.fillRect(x * skala, y * skala, skala + 0.5, skala + 0.5);
      }
    }
  }

  if (ziel) {
    ctx.fillStyle = f.glut;
    ctx.beginPath();
    ctx.arc(ziel.x * skala, ziel.y * skala, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = f.tinte;
  ctx.beginPath();
  ctx.arc(spieler.x * skala, spieler.y * skala, 2.6, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = f.tinte;
  ctx.globalAlpha = 0.4;
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, karte.breite * skala - 1, karte.hoehe * skala - 1);
  ctx.restore();
}
